import { useEffect, useState, useContext } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { getTranslation } from "../utils/translations";

// --------- HELPER FUNCTION ---------
const capitalizeWords = (str) => {
  if (!str) return "";
  return str
    .split(" ")
    .map(word =>
      word
        .split("-")
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join("-")
    )
    .join(" ");
};

export default function GuestBookings({ token, providerId, branchId, refresh }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelingId, setCancelingId] = useState(null);

  const [notice, setNotice] = useState("");
  const [confirmCancelId, setConfirmCancelId] = useState(null);

  const { lang } = useContext(LanguageContext);
  const t = getTranslation(lang);

  const showNotice = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 2500);
  };

  useEffect(() => {
    if (!token) return;

    async function fetchBookings() {
      try {
        setLoading(true);
        setError(null);

        // Build URL with optional query parameters safely
        const params = new URLSearchParams();
        if (providerId) params.append("providerId", providerId);
        if (branchId) params.append("branchId", branchId);

        const url = `${process.env.REACT_APP_API_URL}/api/reservations/my${params.toString() ? `?${params.toString()}` : ""}`;

        console.log("Fetching guest bookings from:", url);

        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log("Response status:", res.status);

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          console.error("Fetch error data:", errData);
          throw new Error(errData.message || "Failed to fetch bookings");
        }

        const data = await res.json();

        // Map backend fields to frontend-friendly format
        const mapped = data.map(b => ({
          id: b.reservation_id,
          providerId: b.provider_id,
          providerName: b.provider_name || b.provider_id,
          branchId: b.branch_id,
          branchName: b.branch_name,
          dateTime: b.date_time,
          guests: b.guests,
          phone: b.phone,
          userEmail: b.user_email,
          status: b.status || "pending"
        }));

        // Sort bookings by date
        mapped.sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
        setBookings(mapped);
      } catch (err) {
        console.error(err);
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    }

    fetchBookings();
  }, [token, providerId, branchId, refresh]);

  const handleCancelBooking = async (reservationId) => {
    try {
      setCancelingId(reservationId);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/reservations/${reservationId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to cancel reservation");
      }

      setBookings(prev => prev.filter(b => b.id !== reservationId));
    } catch (err) {
      console.error(err);
      showNotice(err.message || "Failed to cancel reservation");
    } finally {
      setCancelingId(null);
      setConfirmCancelId(null);
    }
  };

  // ---------------- RENDER -----------------
  if (!token) return <p style={{ color: "#9ca3af", fontWeight: "bold" }}>{t.guest_bookings.login_required}</p>;
  if (loading) return <p>{t.guest_bookings.loading}</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!loading && bookings.length === 0) return <p style={{ color: "#9ca3af", fontWeight: "bold" }}>{t.guest_bookings.no_reservations}</p>;

  return (
    <>
      {notice && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "max-content",
            maxWidth: "calc(100vw - 32px)",
            textAlign: "center",
            background: "#ffffff",
            color: "#111",
            padding: "12px 16px",
            borderRadius: "10px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 9999,
            fontSize: "14px",
            fontWeight: "500"
          }}
        >
          {notice}
        </div>
      )}

      {confirmCancelId && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "transparent",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000
          }}
        >
          <div
            style={{
              background: "#ffffff",
              padding: "20px",
              borderRadius: "10px",
              width: "320px",
              maxWidth: "calc(100vw - 32px)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              textAlign: "center"
            }}
          >
            <p style={{ color: "#111", marginBottom: "20px", fontWeight: "500" }}>
              {t.guest_bookings.confirm_cancel}
            </p>

            <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
              <button
                onClick={() => setConfirmCancelId(null)}
                style={{
                  padding: "8px 14px",
                  background: "#6b7280",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                {t.guest_bookings.cancel}
              </button>

              <button
                onClick={() => handleCancelBooking(confirmCancelId)}
                style={{
                  padding: "8px 14px",
                  background: "#ef4444",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                {t.guest_bookings.confirm}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {bookings.map((b) => (
          <div
            key={b.id}
            style={{
              background: "#e5e7eb",
              color: "#000",
              padding: 12,
              borderRadius: 8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <div>
              <p><strong>{t.guest_bookings.provider}:</strong> {capitalizeWords(b.providerName)}</p>
              <p><strong>{t.guest_bookings.branch}:</strong> {capitalizeWords(b.branchName)}</p>
              <p><strong>{t.guest_bookings.date_time}:</strong> {new Date(b.dateTime).toLocaleString()}</p>
              <p>
                <strong>{t.guest_bookings.status}:</strong>{" "}
                {b.status === "confirmed" ? (
                  <span style={{ color: "#16a34a", fontWeight: "bold" }}>
                    {t.guest_bookings.confirmed} ✅
                  </span>
                ) : (
                  <span style={{ color: "#f59e0b", fontWeight: "bold" }}>
                    {t.guest_bookings.pending}
                  </span>
                )}
              </p>
              {b.phone && <p><strong>{t.guest_bookings.reserver_phone}:</strong> {b.phone}</p>}
              <p><strong>{t.guest_bookings.number_of_guests}:</strong> {b.guests}</p>
            </div>
            <button
              onClick={() => setConfirmCancelId(b.id)}
              disabled={cancelingId === b.id}
              style={{
                padding: "6px 12px",
                background: cancelingId === b.id ? "#f97316" : "#ef4444",
                color: "#fff",
                borderRadius: 6,
                cursor: cancelingId === b.id ? "not-allowed" : "pointer",
                fontWeight: "bold"
              }}
            >
              {cancelingId === b.id ? t.guest_bookings.cancelling : t.guest_bookings.cancel_reservation}
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
